import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material';
import { AgColor } from '../AgColor';
import {
  CSSProperties,
  ElementType,
  LegacyRef,
  MouseEventHandler,
  ReactNode
} from 'react';
import { useAppSelector } from '../../../redux/hooks';
import { selectThemeColors } from '../../../redux/slices/global';

export const styleMappings = {
  'AP.H1.Bold': {
    fontWeight: 700,
    lineHeight: '44px',
    fontSize: '32px'
  },
  'AP.H1.Medium': {
    fontWeight: 500,
    lineHeight: '44px',
    fontSize: '32px'
  },
  'AP.H2.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '28px',
    fontSize: '20px',
    letterSpacing: '0.4px'
  },
  'AP.H2.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '28px',
    fontSize: '20px',
    letterSpacing: '0.4px'
  },
  'AP.H3.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '18px',
    fontSize: '14px',
    letterSpacing: '0.56px'
  },
  'AP.H3.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '18px',
    fontSize: '14px',
    letterSpacing: '0.56px'
  },
  'AP.H4.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '16px',
    fontSize: '12px',
    letterSpacing: '0.48px'
  },
  'AP.H4.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '16px',
    fontSize: '12px',
    letterSpacing: '0.48px'
  },
  'AP.H4.UPPERCASE.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '16px',
    fontSize: '12px',
    letterSpacing: '0.48px',
    textTransform: 'uppercase'
  },
  'AP.H4.UPPERCASE.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '16px',
    fontSize: '12px',
    letterSpacing: '0.48px',
    textTransform: 'uppercase'
  },
  'AP.H5.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '20px',
    fontSize: '10px',
    letterSpacing: '0.4px'
  },
  'AP.H5.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '20px',
    fontSize: '10px',
    letterSpacing: '0.4px'
  },
  'AP.H5.UPPERCASE.Bold': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 700,
    lineHeight: '20px',
    fontSize: '10px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase'
  },
  'AP.H5.UPPERCASE.Medium': {
    fontFamily: 'Roboto, Sans-Serif',
    fontWeight: 500,
    lineHeight: '20px',
    fontSize: '10px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase'
  },
  'AP.Paragraph.Large.Regular': {
    lineHeight: '26px',
    fontSize: '16px',
    fontFamily: 'Inter',
    fontWeight: 400
  },
  'AP.Paragraph.Medium.Regular': {
    lineHeight: '26px',
    fontSize: '14px'
  },
  'AP.Paragraph.Small.Regular': {
    lineHeight: '18px',
    fontSize: '12px'
  },
  'AP.Assistive.Text.Regular': {
    lineHeight: '20px',
    fontSize: '10px'
  },
  'AP.Paragraph.Large.SemiBold': {
    fontWeight: 600,
    lineHeight: '26px',
    fontSize: '16px'
  },
  'AP.Paragraph.Medium.SemiBold': {
    fontWeight: 600,
    lineHeight: '26px',
    fontSize: '14px'
  },
  'AP.Paragraph.Small.SemiBold': {
    fontWeight: 600,
    lineHeight: '18px',
    fontSize: '12px'
  },
  'AP.Assistive.Text.SemiBold': {
    fontWeight: 600,
    lineHeight: '20px',
    fontSize: '10px'
  },
  'AP.Paragraph.Large.Link': {
    fontWeight: 600,
    lineHeight: '26px',
    fontSize: '16px',
    textDecoration: 'underline',
    color: AgColor.link1
  },
  'AP.Paragraph.Medium.Link': {
    fontWeight: 600,
    lineHeight: '26px',
    fontSize: '14px',
    textDecoration: 'underline',
    color: AgColor.link1
  },
  'AP.Paragraph.Small.Link': {
    fontWeight: 600,
    lineHeight: '18px',
    fontSize: '12px',
    textDecoration: 'underline',
    color: AgColor.link1
  }
};

export interface AgTypographyProps {
  id?: number | string;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
  component?: ElementType;
  message?: string | number;
  children?: ReactNode;
  style?: CSSProperties | undefined;
  link?: string;
  target?: string;
  onClick?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  sx?: SxProps<Theme>;
  ref?: LegacyRef<HTMLDivElement> | undefined;
  dataTestId?:string;
  contentStyle?:
    | 'AP.H1.Bold'
    | 'AP.H1.Medium'
    | 'AP.H2.Bold'
    | 'AP.H2.Medium'
    | 'AP.H3.Bold'
    | 'AP.H3.Medium'
    | 'AP.H4.Bold'
    | 'AP.H4.Medium'
    | 'AP.H4.UPPERCASE.Bold'
    | 'AP.H4.UPPERCASE.Medium'
    | 'AP.H5.Bold'
    | 'AP.H5.Medium'
    | 'AP.H5.UPPERCASE.Bold'
    | 'AP.H5.UPPERCASE.Medium'
    | 'AP.Paragraph.Large.Regular'
    | 'AP.Paragraph.Medium.Regular'
    | 'AP.Paragraph.Small.Regular'
    | 'AP.Assistive.Text.Regular'
    | 'AP.Paragraph.Large.SemiBold'
    | 'AP.Paragraph.Medium.SemiBold'
    | 'AP.Paragraph.Small.SemiBold'
    | 'AP.Assistive.Text.SemiBold'
    | 'AP.Paragraph.Large.Link'
    | 'AP.Paragraph.Medium.Link'
    | 'AP.Paragraph.Small.Link'
    | undefined;
}

interface StyleProps {
  fontFamily: string;
  fontWeight: number;
  lineHeight: string;
  fontSize: string;
  letterSpacing: string;
  textTransform: 'none' | 'uppercase';
  color: string;
}

const AgTypography = ({
  id,
  gutterBottom,
  noWrap,
  paragraph,
  component = 'div',
  message,
  children,
  link,
  onClick,
  sx,
  style,
  contentStyle,
  dataTestId,
  target,
  onMouseLeave,
  onMouseEnter,
}: AgTypographyProps) => {
  useAppSelector(selectThemeColors);
  let predefinedStyle: StyleProps = {
    fontFamily: 'Inter, Sans-Serif',
    fontWeight: 400,
    lineHeight: '26px',
    fontSize: '14px',
    letterSpacing: '0',
    textTransform: 'none',
    color: AgColor.grey9
  };

  if (contentStyle && styleMappings[contentStyle]) {
    predefinedStyle = {
      ...predefinedStyle,
      ...styleMappings[contentStyle]
    } as StyleProps;
  }

  if (
    contentStyle &&
    [
      'AP.Paragraph.Large.Link',
      'AP.Paragraph.Medium.Link',
      'AP.Paragraph.Small.Link'
    ].includes(contentStyle)
  ) {
    return (
      <a
        data-testid={dataTestId}
        style={{
          textDecorationColor: AgColor.secondaryMain,
        }}
        href={link}
        target={target}
        onClick={onClick}
      >
        <Typography
          id={id}
          noWrap={noWrap}
          paragraph={paragraph}
          component={component}
          gutterBottom={gutterBottom}
          onClick={link ? undefined : onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sx={sx}
          style={{
            ...predefinedStyle,
            ...style
          }}
        >
          {children}
          {message}
        </Typography>
      </a>
    );
  }

  return (
    <Typography
      id={id}
      noWrap={noWrap}
      paragraph={paragraph}
      component={component}
      gutterBottom={gutterBottom}
      onClick={onClick}
      style={{ ...predefinedStyle, ...style }}
      sx={sx}
      data-testid={dataTestId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
      {message}
    </Typography>
  );
};

export default AgTypography;
